import React, { useMemo, useState } from 'react';
import InfoTip from '../../components/InfoTip';
import DetailModal from './DetailModal';
import {
  aggProjets, buildCommerciaux, projetsEnRetard, tauxCommercialisation, comActif,
  fmtMt, fmtNb, pct, fmtDateFr, moisLabel, RETARD_SEUIL, evolution,
} from './format';

// Contenu chiffré du Point du jour : cartes + tableau ville/projet + commerciaux + alertes.
// Utilisé par la page de préparation (tableau de contrôle) et par la page /rapport/:token.
// Chaque chiffre est cliquable : détail figé (data.details) ou, pour mois / cumul vs objectif,
// détail chargé à la demande si canLive (accès Décisionnel).

const BROWN = '#6d4e36';
export const pctColor = (p) => (p == null ? '#888' : p >= 100 ? '#2e7d32' : p >= 70 ? '#e65100' : '#c62828');

// Chiffre cliquable (ouvre la fenêtre de détail) ; inerte si onClick absent
export const Clic = ({ onClick, children }) => (onClick
  ? <span role="button" tabIndex={0} onClick={onClick} onKeyDown={e => e.key === 'Enter' && onClick()}
      title="Voir le détail" style={{ cursor: 'pointer', borderBottom: '1px dotted currentColor' }}>{children}</span>
  : <span>{children}</span>);

const Pct = ({ r, o }) => {
  const p = pct(r, o);
  return <span style={{ color: pctColor(p), fontWeight: 600 }}>{p == null ? '—' : `${p} %`}</span>;
};

const ObjCell = ({ ind, per = 'm', onClick }) => (
  <td style={{ whiteSpace: 'nowrap' }}>
    <Clic onClick={ind[per].r ? onClick : null}>{fmtMt(ind[per].r)}</Clic> <small className="text-muted">/ {fmtMt(ind[per].o)}</small><br />
    <Pct r={ind[per].r} o={ind[per].o} />
  </td>
);

const KpiObj = ({ title, info, ind, onClick }) => {
  const p = pct(ind.m.r, ind.m.o);
  return (
    <div className="col-12 col-sm-4 mb-2">
      <div style={{ border: '1px solid #e0d6c8', borderTop: `4px solid ${pctColor(p)}`, borderRadius: 10, padding: '10px 14px', background: '#fff', height: '100%' }}>
        <div style={{ fontSize: '0.8rem', color: BROWN, fontWeight: 600 }}>{title} <InfoTip text={info} /></div>
        <div style={{ fontSize: '1.6rem', fontWeight: 700, color: pctColor(p) }}>{p == null ? '—' : `${p} %`}</div>
        <div style={{ fontSize: '0.8rem', color: '#666' }}>
          <Clic onClick={ind.m.r ? onClick : null}>{fmtMt(ind.m.r)}</Clic> / {fmtMt(ind.m.o)} DH · cumul {fmtMt(ind.c.r)} ({pct(ind.c.r, ind.c.o) ?? '—'} %)
        </div>
      </div>
    </div>
  );
};

export const Stat = ({ label, value, sub, alert, info, onClick }) => (
  <div className="col-6 col-md mb-2">
    <div style={{ background: alert ? '#fdecea' : '#f9f6ef', borderRadius: 8, padding: '8px 12px', height: '100%' }}>
      <div style={{ fontSize: '0.75rem', color: BROWN }}>{label} <InfoTip text={info} /></div>
      <div style={{ fontSize: '1.15rem', fontWeight: 700, color: alert ? '#c62828' : BROWN }}><Clic onClick={onClick}>{value}</Clic></div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#777' }}>{sub}</div>}
    </div>
  </div>
);

export const SectionTitle = ({ children }) => (
  <h6 style={{ color: BROWN, fontWeight: 700, margin: '18px 0 8px', borderBottom: '2px solid #f5f5db', paddingBottom: 4 }}>{children}</h6>
);

const LIVE_LABELS = { ventes: 'Ventes', encRecouv: 'Encaissement + Recouvrement', liv: 'Livraisons' };

const RapportView = ({ data, showCommerciaux = true, canLive = false }) => {
  const [detail, setDetail] = useState(null);

  const calc = useMemo(() => {
    if (!data) return null;
    const all = new Set(data.projets.map(p => p.id));
    const villes = [];
    for (const p of data.projets) {
      let v = villes.find(x => x.villeId === p.villeId);
      if (!v) { v = { villeId: p.villeId, ville: p.ville, projets: [] }; villes.push(v); }
      v.projets.push({ ...p, ids: new Set([p.id]), agg: aggProjets(data, new Set([p.id])) });
    }
    villes.forEach(v => { v.ids = new Set(v.projets.map(p => p.id)); v.agg = aggProjets(data, v.ids); });
    return {
      allIds: all,
      total: aggProjets(data, all),
      villes,
      commerciaux: buildCommerciaux(data)
        // Sans aucun chiffre (hier, mois, cumul) → masqué
        .filter(c => comActif(c, 'm') || comActif(c, 'c') || c.hier.ventesNb || c.hier.encMt)
        .sort((a, b) => (pct(b.ventes.m.r, b.ventes.m.o) ?? -1) - (pct(a.ventes.m.r, a.ventes.m.o) ?? -1)),
      retard: projetsEnRetard(data),
    };
  }, [data]);

  if (!calc) return null;
  const T = calc.total;
  const allIds = calc.allIds;

  // Détail figé (kind) ; null si le rapport n'a pas de lignes de détail
  const voir = (kind, titre, ids = allIds, extra = {}) =>
    (data.details ? () => setDetail({ kind, titre, ids, ...extra }) : null);
  // Idem, seulement si le chiffre est non nul
  const voirSi = (n, kind, titre, ids, extra) => (n ? voir(kind, titre, ids, extra) : null);
  // Détail mois / cumul vs objectif, chargé à la demande
  const voirLive = (ind, periode, titre, ids = allIds, row = 'total') => (canLive
    ? () => setDetail({
      titre: `${LIVE_LABELS[ind]} — ${periode === 'm' ? moisLabel(data) : `cumul ${data.annee}`} — ${titre}`,
      ids, live: { ind, periode, row },
    })
    : null);

  const ligne = (label, a, ids, { bold, indent } = {}) => (
    <tr key={label} style={bold ? { fontWeight: 700, background: '#f5f5db' } : undefined}>
      <td style={{ textAlign: 'left', paddingLeft: indent ? 24 : 8, whiteSpace: 'nowrap' }}>{bold && label !== 'TOTAL' ? `🏙 ${label}` : label}</td>
      <td><Clic onClick={voirSi(a.hier.ventesNb, 'ventes', `Ventes d'hier — ${label}`, ids)}>{fmtNb(a.hier.ventesNb)} · {fmtMt(a.hier.ventesMt)}</Clic></td>
      <td><Clic onClick={voirSi(a.hier.encMt, 'enc', `Encaissements d'hier — ${label}`, ids)}>{fmtMt(a.hier.encMt)}</Clic></td>
      <ObjCell ind={a.obj.ventes} onClick={voirLive('ventes', 'm', label, ids)} />
      <ObjCell ind={a.obj.encRecouv} onClick={voirLive('encRecouv', 'm', label, ids)} />
      <ObjCell ind={a.obj.liv} onClick={voirLive('liv', 'm', label, ids)} />
      <td>{a.ptf.nb7
        ? <Clic onClick={voir('ptf7', `Chèques PTF sous 7 jours — ${label}`, ids)}>{fmtNb(a.ptf.nb7)} · {fmtMt(a.ptf.mt7)}</Clic>
        : '—'}</td>
      <td style={{ color: a.notaire.nbBloque ? '#c62828' : undefined }}>
        <Clic onClick={voirSi(a.notaire.nbBloque, 'notaireBloque', `Notaire > ${data.seuils.notaireBloque} j — ${label}`, ids)}>{fmtNb(a.notaire.nbBloque)}</Clic>
      </td>
      <td style={{ color: a.impayes.nb ? '#c62828' : undefined }}>{a.impayes.nb
        ? <Clic onClick={voir('impayesEnCours', `Impayés en cours — ${label}`, ids)}>{fmtNb(a.impayes.nb)} · {fmtMt(a.impayes.mt)}</Clic>
        : '—'}</td>
      <td>
        <Clic onClick={voirSi(a.stock.dispo, 'stockDispo', `Lots disponibles — ${label}`, ids)}>{fmtNb(a.stock.dispo)}</Clic>{' '}
        <small className="text-muted">({tauxCommercialisation(a.stock) ?? '—'} %)</small>
      </td>
    </tr>
  );

  return (
    <div>
      <SectionTitle>📈 {moisLabel(data)} — au {fmtDateFr(data.date)}</SectionTitle>
      <div className="row">
        <KpiObj title="Ventes" ind={T.obj.ventes} onClick={voirLive('ventes', 'm', 'Total')}
          info="Prix net des contrats + prix des parts d'association réservés du 1er du mois au jour de référence, comparé à l'objectif du mois (goalV). Même calcul que « Performance par commercial »." />
        <KpiObj title="Encaissement + Recouvrement" ind={T.obj.encRecouv} onClick={voirLive('encRecouv', 'm', 'Total')}
          info="Montant des encaissements saisis (DATE_SAISIE) du 1er du mois au jour de référence, hors IMPAYE / RECUPERE / A_NE_PAS_VERSER. Objectif = goalE + goalR." />
        <KpiObj title="Livraisons" ind={T.obj.liv} onClick={voirLive('liv', 'm', 'Total')}
          info="Ventes passées chez le notaire (chez notaire, signature en cours ou signé) dans le mois, comparées à l'objectif goalL." />
      </div>

      {data.comparaison && (
        <div className="small mb-2" style={{ color: '#555' }}>
          ↔️ <strong>Comparaison</strong> avec le {fmtDateFr(data.comparaison.start)} → {fmtDateFr(data.comparaison.end)} :{' '}
          ventes {fmtMt(T.n1.ventes)} DH{evolution(T.obj.ventes.m.r, T.n1.ventes)} · enc. + recouv. {fmtMt(T.n1.enc)} DH{evolution(T.obj.encRecouv.m.r, T.n1.enc)}
          {' '}<InfoTip text="Ventes et encaissements de la période de comparaison (mêmes règles que « mois vs objectif »), et variation du mois en cours par rapport à elle." />
        </div>
      )}

      <SectionTitle>🗓 Hier ({fmtDateFr(data.date)})</SectionTitle>
      <div className="row">
        <Stat label="Ventes" value={`${fmtNb(T.hier.ventesNb)} · ${fmtMt(T.hier.ventesMt)}`}
          onClick={voirSi(T.hier.ventesNb, 'ventes', "Ventes d'hier")}
          info="Nombre de lots / parts réservés le jour de référence et montant (DH)." />
        <Stat label="Encaissements" value={`${fmtMt(T.hier.encMt)} DH`}
          onClick={voirSi(T.hier.encMt, 'enc', "Encaissements d'hier")}
          info="Encaissements saisis le jour de référence (hors impayés)." />
        <Stat label="Passages notaire" value={fmtNb(T.hier.livNb)}
          onClick={voirSi(T.hier.livNb, 'liv', "Passages notaire d'hier")}
          info="Ventes dont la date notaire (dépôt, signature en cours ou signature) est le jour de référence." />
        <Stat label="Nouveaux impayés" value={fmtNb(T.hier.impNb)} sub={T.hier.impNb ? `${fmtMt(T.hier.impMt)} DH` : null} alert={T.hier.impNb > 0}
          onClick={voirSi(T.hier.impNb, 'impayes', "Nouveaux impayés d'hier")}
          info="Encaissements passés au statut IMPAYE avec une date d'impayé égale au jour de référence." />
        <Stat label="Désistements" value={fmtNb(T.hier.desistNb)} alert={T.hier.desistNb > 0}
          onClick={voirSi(T.hier.desistNb, 'desist', "Désistements d'hier")}
          info="Désistements créés le jour de référence (non annulés)." />
      </div>

      <SectionTitle>🔔 À surveiller</SectionTitle>
      <div className="row">
        <Stat label="Chèques PTF sous 7 j" value={fmtNb(T.ptf.nb7)} sub={`${fmtMt(T.ptf.mt7)} DH · 30 j : ${fmtNb(T.ptf.nb30)}`}
          onClick={voirSi(T.ptf.nb7, 'ptf7', 'Chèques PTF à échéance sous 7 jours')}
          info="Chèques au statut PTF (portefeuille) dont la date de règlement tombe dans les 7 jours suivant le jour de référence." />
        <Stat label="Notaire non soldé" value={fmtNb(T.notaire.nbReliquat)} sub={`reliquat ${fmtMt(T.notaire.reliquat)} DH`}
          onClick={voirSi(T.notaire.nbReliquat, 'notaireReliquat', 'Dossiers chez le notaire non soldés')}
          info={`Dossiers chez le notaire, non signés, dont le reliquat (prix net − avances) dépasse ${data.seuils.reliquatMin} DH.`} />
        <Stat label={`Notaire > ${data.seuils.notaireBloque} j`} value={fmtNb(T.notaire.nbBloque)} alert={T.notaire.nbBloque > 0} sub={`sur ${fmtNb(T.notaire.nb)} dossiers`}
          onClick={voirSi(T.notaire.nbBloque, 'notaireBloque', `Dossiers chez le notaire depuis plus de ${data.seuils.notaireBloque} jours`)}
          info={`Dossiers chez le notaire, non signés, déposés depuis plus de ${data.seuils.notaireBloque} jours.`} />
        <Stat label="Impayés en cours" value={fmtNb(T.impayes.nb)} sub={`${fmtMt(T.impayes.mt)} DH · + 90 j : ${fmtNb(T.impayes.nbPlus)}`} alert={T.impayes.nb > 0}
          onClick={voirSi(T.impayes.nb, 'impayesEnCours', 'Impayés en cours')}
          info="Encaissements actuellement au statut IMPAYE. Ancienneté calculée depuis la date d'impayé (à défaut la date de règlement)." />
      </div>
      {calc.retard.length > 0 && (
        <div style={{ fontSize: '0.85rem', margin: '4px 0 8px' }}>
          <strong style={{ color: '#c62828' }}>Projets en retard</strong>{' '}
          <InfoTip text={`Réalisé du mois inférieur à ${RETARD_SEUIL} % de l'objectif attendu à date (objectif du mois × jours écoulés / jours du mois).`} />
          {' : '}
          {calc.retard.map((r, i) => (
            <React.Fragment key={r.projetId}>
              {i > 0 && ' · '}
              <Clic onClick={voirLive(r.indicateur === 'Ventes' ? 'ventes' : 'encRecouv', 'm', r.projet, new Set([r.projetId]))}>
                {r.projet} ({r.indicateur} {r.pctAttendu} %)
              </Clic>
            </React.Fragment>
          ))}
        </div>
      )}

      <SectionTitle>🏙 Par ville et par projet</SectionTitle>
      <div className="table-responsive">
        <table className="table table-bordered table-sm" style={{ fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Ville / Projet <InfoTip text="Cliquez sur un chiffre souligné pour voir les lignes qui le composent." /></th>
              <th>Ventes hier <InfoTip text="Nombre · montant des ventes du jour de référence." /></th>
              <th>Enc. hier</th>
              <th>Ventes mois <InfoTip text="Réalisé / objectif du mois et % atteint." /></th>
              <th>Enc+Rec mois</th>
              <th>Livr. mois</th>
              <th>PTF 7 j</th>
              <th>Notaire &gt; {data.seuils.notaireBloque} j</th>
              <th>Impayés</th>
              <th>Stock dispo <InfoTip text="Lots au statut DISPONIBLE (taux de commercialisation entre parenthèses : vendus / (vendus + disponibles + bloqués))." /></th>
            </tr>
          </thead>
          <tbody>
            {calc.villes.map(v => (
              <React.Fragment key={v.villeId}>
                {ligne(v.ville, v.agg, v.ids, { bold: true })}
                {v.projets.length > 1 && v.projets.map(p => ligne(p.nom, p.agg, p.ids, { indent: true }))}
              </React.Fragment>
            ))}
            {ligne('TOTAL', T, allIds, { bold: true })}
          </tbody>
        </table>
      </div>

      {showCommerciaux && calc.commerciaux.length > 0 && (
        <>
          <SectionTitle>👥 Commerciaux</SectionTitle>
          <div className="table-responsive">
            <table className="table table-bordered table-sm" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Commercial <InfoTip text="« Autres » = ventes / encaissements sans commercial ou d'un commercial non affecté au projet." /></th>
                  <th>Ventes hier</th>
                  <th>Enc. hier</th>
                  <th>Ventes mois</th>
                  <th>Enc+Rec mois</th>
                  <th>Livr. mois</th>
                  <th>Ventes cumul</th>
                </tr>
              </thead>
              <tbody>
                {calc.commerciaux.map(c => {
                  const comId = c.kind === 'com' ? Number(c.id) : null;
                  return (
                    <tr key={c.id}>
                      <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>{c.commercial}</td>
                      <td>{c.hier.ventesNb
                        ? <Clic onClick={voir('ventes', `Ventes d'hier — ${c.commercial}`, allIds, { comId })}>{fmtNb(c.hier.ventesNb)} · {fmtMt(c.hier.ventesMt)}</Clic>
                        : '—'}</td>
                      <td>{c.hier.encMt
                        ? <Clic onClick={voir('enc', `Encaissements d'hier — ${c.commercial}`, allIds, { comId })}>{fmtMt(c.hier.encMt)}</Clic>
                        : '—'}</td>
                      <ObjCell ind={c.ventes} onClick={voirLive('ventes', 'm', c.commercial, allIds, c.id)} />
                      <ObjCell ind={c.encRecouv} onClick={voirLive('encRecouv', 'm', c.commercial, allIds, c.id)} />
                      <ObjCell ind={c.liv} onClick={voirLive('liv', 'm', c.commercial, allIds, c.id)} />
                      <ObjCell ind={c.ventes} per="c" onClick={voirLive('ventes', 'c', c.commercial, allIds, c.id)} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <DetailModal data={data} detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
};

export default RapportView;
